import {
  ArrowRight,
  Check,
  Heart,
  MessageCircle,
  Quote,
  Shield,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import { motion, useScroll, useTransform } from "motion/react";
import { useEffect, useState } from "react";

import mobileDemo from "../assets/mobile-demo.gif";
import {
  trackEngagement,
  trackFunnelStep,
  trackPageView,
} from "../utils/analytics";

import { KoruLogo } from "./brand/KoruLogo";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

interface LandingPageProps {
  onTryApp: () => void;
  isLoading?: boolean;
}

export default function LandingPage({
  onTryApp,
  isLoading = false,
}: LandingPageProps) {
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollDepth, setScrollDepth] = useState(0);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 50]);

  // Track page view on component mount (using centralized analytics)
  useEffect(() => {
    trackPageView("/landing", "Koru - Transform Your Relationships");
  }, []);

  // Track scroll depth and engagement
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.body.offsetHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      // Track scroll depth milestones
      if (scrollPercent >= 25 && scrollDepth < 25) {
        setScrollDepth(25);
        trackEngagement("scroll", "landing_page", {
          page: "landing",
          scroll_depth: 25,
        });
        trackFunnelStep("landing_engagement", { scroll_depth: 25 });
      } else if (scrollPercent >= 50 && scrollDepth < 50) {
        setScrollDepth(50);
        trackEngagement("scroll", "landing_page", {
          page: "landing",
          scroll_depth: 50,
        });
        trackFunnelStep("landing_engagement", { scroll_depth: 50 });
      } else if (scrollPercent >= 75 && scrollDepth < 75) {
        setScrollDepth(75);
        trackEngagement("scroll", "landing_page", {
          page: "landing",
          scroll_depth: 75,
        });
        trackFunnelStep("landing_engagement", { scroll_depth: 75 });
      } else if (scrollPercent >= 90 && scrollDepth < 90) {
        setScrollDepth(90);
        trackEngagement("scroll", "landing_page", {
          page: "landing",
          scroll_depth: 90,
        });
        trackFunnelStep("landing_engagement", { scroll_depth: 90 });
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollDepth]);

  const trustedUsers = [
    {
      name: "Sarah Chen",
      role: "Therapist",
      initials: "SC",
      color: "from-primary to-accent",
      testimonial:
        "Koru has transformed how I help couples communicate. The AI guidance is incredibly nuanced and effective.",
    },
    {
      name: "Marcus Johnson",
      role: "Co-Parent",
      initials: "MJ",
      color: "from-secondary to-primary",
      testimonial:
        "Finally found a tool that helps us communicate better for our kids. Game-changing for co-parenting.",
    },
    {
      name: "Dr. Emily Rodriguez",
      role: "Relationship Coach",
      initials: "ER",
      color: "from-accent to-secondary",
      testimonial:
        "I recommend Koru to all my clients. It's like having a communication coach available 24/7.",
    },
    {
      name: "Alex Thompson",
      role: "Marriage Counselor",
      initials: "AT",
      color: "from-primary to-secondary",
      testimonial:
        "The AI's ability to detect emotional patterns and suggest interventions is remarkable.",
    },
    {
      name: "Jessica Kim",
      role: "Life Coach",
      initials: "JK",
      color: "from-accent to-primary",
      testimonial:
        "My clients see breakthrough moments in their relationships using Koru's guided conversations.",
    },
    {
      name: "David Park",
      role: "Family Therapist",
      initials: "DP",
      color: "from-secondary to-accent",
      testimonial:
        "Koru bridges the gap between sessions. Families practice healthy communication at home.",
    },
  ];

  const features = [
    {
      icon: MessageCircle,
      title: "AI-Guided Conversations",
      description:
        "Smart prompts and real-time guidance help you navigate difficult conversations with empathy and clarity.",
      color: "primary",
    },
    {
      icon: Heart,
      title: "Relationship Analytics",
      description:
        "Track communication patterns, emotional trends, and relationship health with detailed insights.",
      color: "secondary",
    },
    {
      icon: Users,
      title: "Multi-Party Support",
      description:
        "Facilitate group conversations between couples, families, teams, and close friends.",
      color: "accent",
    },
    {
      icon: Shield,
      title: "Privacy-First Design",
      description:
        "End-to-end encryption and privacy controls ensure your conversations stay confidential.",
      color: "primary",
    },
  ];

  const detailedTestimonials = [
    {
      name: "Sarah Chen",
      role: "Licensed Therapist • 15+ years experience",
      content:
        "Koru has revolutionized my practice. The AI's emotional intelligence helps couples break through communication barriers I've seen stall progress for months. My success rate has increased by 40%.",
      rating: 5,
      avatar: "SC",
      color: "from-primary to-accent",
    },
    {
      name: "Marcus Johnson",
      role: "Divorced Co-Parent • Father of 2",
      content:
        "After our divorce, communicating with my ex about the kids was impossible. Koru's guided sessions helped us focus on what matters - our children's wellbeing. Now we actually collaborate.",
      rating: 5,
      avatar: "MJ",
      color: "from-secondary to-primary",
    },
    {
      name: "Dr. Emily Rodriguez",
      role: "Relationship Psychology PhD",
      content:
        "I was skeptical about AI in therapy, but Koru's approach is grounded in solid psychological principles. It's not replacing human connection - it's enhancing it beautifully.",
      rating: 5,
      avatar: "ER",
      color: "from-accent to-secondary",
    },
  ];

  const pricingTiers = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Perfect for getting started",
      features: [
        "5 guided conversations per month",
        "Basic AI insights",
        "1-on-1 conversations only",
        "Email support",
      ],
      cta: "Start Free",
      popular: false,
    },
    {
      name: "Premium",
      price: "$9.99",
      period: "per month",
      description: "For regular communication improvement",
      features: [
        "Unlimited guided conversations",
        "Advanced AI insights & analytics",
        "Group conversations (up to 4 people)",
        "Priority support",
        "Custom conversation templates",
        "Export conversation summaries",
      ],
      cta: "Start Premium Trial",
      popular: true,
    },
    {
      name: "Committed",
      price: "$19.99",
      period: "per month",
      description: "For serious relationship building",
      features: [
        "Everything in Premium",
        "Unlimited participants",
        "Advanced relationship analytics",
        "Personal AI coach customization",
        "Integration with calendar & reminders",
        "Priority feature requests",
        "Dedicated relationship specialist",
      ],
      cta: "Go Committed",
      popular: false,
    },
  ];

  // Auto-rotate active feature
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    try {
      timer = setInterval(() => {
        setActiveFeature(prev => (prev + 1) % features.length);
      }, 4000);
    } catch (error) {
      console.error("Error in feature rotation:", error);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [features.length]);

  const handleTryApp = () => {
    trackEngagement("click", "get_started_button", {
      page: "landing",
      section: "navigation",
      source: "landing_page",
    });
    trackFunnelStep("waitlist_start", { source: "get_started_button" });
    onTryApp();
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden w-full max-w-full">
      {/* Navigation */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-lg border-b border-border overflow-x-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between w-full box-border">
          <motion.div
            className="flex flex-col items-center space-y-1"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <KoruLogo className="svg-glow-primary w-10 h-8 sm:w-12 sm:h-10" />
            <span className="text-sm sm:text-base font-semibold text-foreground leading-none">
              Koru
            </span>
          </motion.div>

          <div className="flex items-center">
            <Button
              onClick={handleTryApp}
              disabled={isLoading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary text-sm sm:text-base px-3 sm:px-4 py-2"
            >
              <span className="hidden sm:inline">
                {isLoading ? "Loading..." : "Get Started"}
              </span>
              <span className="sm:hidden">{isLoading ? "..." : "Start"}</span>
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
            </Button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-24 pb-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto w-full box-border">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI-Powered Communication
                  </Badge>
                </motion.div>

                <motion.h1
                  className="text-4xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  Transform Your
                  <br />
                  <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                    Relationships
                  </span>
                </motion.h1>

                <motion.p
                  className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  Koru&apos;s AI Guide helps couples, co-parents, and close
                  friends have deeper, more meaningful conversations that
                  strengthen bonds and resolve conflicts.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  onClick={handleTryApp}
                  disabled={isLoading}
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary px-6 sm:px-8 py-6 text-lg"
                >
                  {isLoading ? "Loading..." : "Start Your Journey"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              {/* Trusted Users Section - Enhanced */}
              <motion.div
                className="pt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Trusted by relationship professionals and 12,000+ users
                  </p>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                    <div className="flex -space-x-3">
                      {trustedUsers.slice(0, 6).map((user, index) => (
                        <motion.div
                          key={user.name}
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.7 + index * 0.1 }}
                          whileHover={{ scale: 1.1, zIndex: 10 }}
                          className="relative group"
                        >
                          <Avatar
                            className={`h-10 w-10 sm:h-12 sm:w-12 border-2 border-background cursor-pointer`}
                          >
                            <AvatarFallback
                              className={`bg-gradient-to-br ${user.color} text-white text-sm font-semibold`}
                            >
                              {user.initials}
                            </AvatarFallback>
                          </Avatar>

                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-card text-xs text-card-foreground rounded-lg border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                            <div className="font-medium">{user.name}</div>
                            <div className="text-muted-foreground">
                              {user.role}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-1">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star
                            key={i}
                            className="w-4 h-4 fill-primary text-primary"
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        4.9/5
                      </span>
                      <span className="text-sm text-muted-foreground">
                        (2,847 reviews)
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Phone Demo */}
            <motion.div
              initial={{ opacity: 0, x: 50, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ y: y1 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px]">
                <div className="h-[32px] w-[3px] bg-gray-800 absolute -left-[17px] top-[72px] rounded-l-lg" />
                <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[124px] rounded-l-lg" />
                <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[178px] rounded-l-lg" />
                <div className="h-[64px] w-[3px] bg-gray-800 absolute -right-[17px] top-[142px] rounded-r-lg" />
                <div className="rounded-[2rem] overflow-hidden w-[272px] h-[572px] bg-gray-900 relative">
                  <img
                    src={mobileDemo}
                    alt="Koru Mobile App Demo"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Floating Elements */}
        <motion.div
          className="absolute top-32 left-4 sm:left-10 w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full blur-xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-4 sm:right-20 w-24 h-24 sm:w-32 sm:h-32 bg-secondary/10 rounded-full blur-xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 bg-card/30">
        <div className="max-w-7xl mx-auto w-full box-border">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Why Choose Koru?
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              Our AI Guide combines cutting-edge technology with proven
              relationship science to help you communicate better, resolve
              conflicts, and deepen connections.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isActive = index === activeFeature;

              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onHoverStart={() => setActiveFeature(index)}
                >
                  <Card
                    className={`h-full border-border hover:border-primary/50 transition-all duration-300 cursor-pointer ${
                      isActive
                        ? "bg-primary/5 border-primary/50 glow-primary"
                        : "bg-card"
                    }`}
                  >
                    <CardContent className="p-6 text-center space-y-4">
                      <motion.div
                        className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                          feature.color === "primary"
                            ? "bg-primary/10 text-primary"
                            : feature.color === "secondary"
                              ? "bg-secondary/10 text-secondary"
                              : "bg-accent/10 text-accent"
                        }`}
                        animate={{ scale: isActive ? 1.1 : 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Icon className="w-8 h-8" />
                      </motion.div>
                      <h3 className="text-foreground font-semibold">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enhanced Testimonials */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto w-full box-border">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Trusted by Relationship Experts
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground">
              See what professionals and users are saying about Koru
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {detailedTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card className="bg-card border-border h-full">
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          className="w-5 h-5 fill-primary text-primary"
                        />
                      ))}
                    </div>

                    <div className="relative">
                      <Quote className="absolute -top-2 -left-1 w-8 h-8 text-primary/20" />
                      <p className="text-foreground italic pl-6">
                        &quot;{testimonial.content}&quot;
                      </p>
                    </div>

                    <div className="flex items-center space-x-4 pt-4 border-t border-border">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback
                          className={`bg-gradient-to-br ${testimonial.color} text-white font-semibold`}
                        >
                          {testimonial.avatar}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-foreground">
                          {testimonial.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 bg-card/30">
        <div className="max-w-7xl mx-auto w-full box-border">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Choose Your Plan
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground">
              Start free and upgrade as your relationships grow stronger
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  className={`relative h-full ${
                    tier.popular
                      ? "bg-primary/5 border-primary glow-primary"
                      : "bg-card border-border"
                  }`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground glow-primary">
                        Most Popular
                      </Badge>
                    </div>
                  )}

                  <CardContent className="p-6 space-y-6">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-foreground mb-2">
                        {tier.name}
                      </h3>
                      <div className="mb-2">
                        <span className="text-4xl font-bold text-foreground">
                          {tier.price}
                        </span>
                        <span className="text-muted-foreground">
                          /{tier.period}
                        </span>
                      </div>
                      <p className="text-muted-foreground">
                        {tier.description}
                      </p>
                    </div>

                    <div className="space-y-3">
                      {tier.features.map(feature => (
                        <div
                          key={feature}
                          className="flex items-center space-x-3"
                        >
                          <Check className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-foreground text-sm sm:text-base">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={handleTryApp}
                      disabled={isLoading}
                      className={`w-full ${
                        tier.popular
                          ? "bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"
                          : "bg-accent text-accent-foreground hover:bg-accent/90"
                      }`}
                    >
                      {isLoading ? "Loading..." : tier.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground">
              Ready to Transform Your Relationships?
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Join thousands of people who are already building stronger, more
              meaningful connections with Koru&apos;s AI-guided communication.
            </p>

            <div className="flex justify-center">
              <Button
                onClick={handleTryApp}
                disabled={isLoading}
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary px-8 sm:px-12 py-6 text-lg"
              >
                {isLoading ? "Loading..." : "Start Free Today"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">
              No credit card required • Free forever plan available
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card/50 border-t border-border py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto w-full box-border">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
            <div className="flex flex-col items-center space-y-2">
              <KoruLogo className="svg-glow-primary w-12 h-10" />
              <span className="text-lg font-semibold text-foreground">
                Koru
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <p className="text-muted-foreground text-center">
                &copy; 2025 Koru. All rights reserved.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="#legal"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Terms & Privacy
                </a>
                <a
                  href="mailto:support@koruapp.com"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
